import React, { useState, useEffect } from 'react';
import { socket } from '../socket';
import { RECENT_MASTERPIECES } from '../featureFlags';
import tincanTelephone from '../assets/tincan-telephone.svg';
import './Home.css';

// Sketchpad palette: coral, brand yellow, sky, link-blue, primary green, deeper brown
const ARTIST_COLORS = ['#EB605C', '#FBE67A', '#5CD1FF', '#3594C0', '#5A8030', '#8BC680'];

// The legal pages are static files in client/public (privacy.html / terms.html),
// served on the web client's own domain via the vercel.json rewrites. Same
// origin, so link relatively — keeps the backend server URL out of the app.
const PRIVACY_URL = '/privacy';
const TERMS_URL = '/terms';

/** Renders a gallery drawing as a flat PNG (server stores base64 PNG bytes
 *  per ARC-77; iOS submissions land here with thumbnailPng=null and show
 *  the placeholder.) */
function GalleryThumbnail({ thumbnailPng }) {
  if (!thumbnailPng) {
    return <div className="gallery-canvas gallery-canvas-empty"><span className="material-symbols-outlined">brush</span></div>;
  }
  return (
    <img
      className="gallery-canvas-real"
      alt=""
      src={`data:image/png;base64,${thumbnailPng}`}
    />
  );
}

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const secs = Math.floor(Date.now() / 1000 - timestamp);
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function Home({ onCreateRoom, onJoinRoom, inviteRoomCode, clearInviteRoomCode, error, clearError }) {
  const [mode, setMode] = useState(inviteRoomCode ? 'join' : null); // null | 'create' | 'join'
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState(inviteRoomCode || '');

  // Widescreen stats
  const [stats, setStats] = useState({
    games_completed: 0,
    drawings_created: 0,
    players_online: 0,
    gallery: [],
    activity: [],
  });

  // Content reporting: which gallery card is showing the confirm prompt, and a
  // transient acknowledgement toast after a report is sent.
  const [reportingId, setReportingId] = useState(null);
  const [reportAck, setReportAck] = useState('');

  useEffect(() => {
    // Request stats on mount
    socket.emit('get_home_stats');

    function onHomeStats(data) {
      setStats(data);
    }

    function onReportReceived(data) {
      setReportingId(null);
      setReportAck(data?.message || 'Thanks — our team will take a look.');
    }

    socket.on('home_stats_updated', onHomeStats);
    socket.on('report_received', onReportReceived);
    return () => {
      socket.off('home_stats_updated', onHomeStats);
      socket.off('report_received', onReportReceived);
    };
  }, []);

  // Auto-dismiss the acknowledgement toast.
  useEffect(() => {
    if (!reportAck) return;
    const t = setTimeout(() => setReportAck(''), 4000);
    return () => clearTimeout(t);
  }, [reportAck]);

  const reportGalleryItem = (itemId) => {
    if (!itemId) return;
    socket.emit('report_gallery_item', { item_id: itemId });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!playerName.trim()) return;
    if (mode === 'create') {
      onCreateRoom(playerName.trim());
    } else if (mode === 'join' && roomCode.trim()) {
      onJoinRoom(playerName.trim(), roomCode.trim().toUpperCase());
    }
  };

  const handleBack = () => {
    setMode(null);
    if (clearInviteRoomCode) clearInviteRoomCode();
    if (clearError) clearError();
    setRoomCode('');
  };

  return (
    <div className="home-container">
      {reportAck && (
        <div className="report-ack-toast" role="status">
          <span className="material-symbols-outlined">check_circle</span>
          {reportAck}
        </div>
      )}

      <div className="widescreen-layout">

        {/* ═══ LEFT PANEL: Gallery ═══ */}
        {RECENT_MASTERPIECES && mode === null && (
        <div className="side-panel side-panel-left gallery-panel">
          <div className="panel-header">
            <div className="panel-header-icon gallery-header-icon">
              <span className="material-symbols-outlined">palette</span>
            </div>
            <h2>Recent Masterpieces</h2>
          </div>

          <div className="gallery-grid">
            {stats.gallery.length > 0 ? stats.gallery.slice(0, 6).map((item, i) => (
              <div className="gallery-card" key={item.id || i}>
                <div className="gallery-canvas-wrapper">
                  <GalleryThumbnail thumbnailPng={item.thumbnail_png} />
                  {item.id && (
                    <button
                      type="button"
                      className="gallery-report-btn"
                      title="Report this drawing"
                      aria-label="Report this drawing"
                      onClick={() => setReportingId(item.id)}
                    >
                      <span className="material-symbols-outlined">flag</span>
                    </button>
                  )}
                  {reportingId === item.id && (
                    <div className="gallery-report-overlay">
                      <p>Report this drawing as inappropriate?</p>
                      <div className="gallery-report-actions">
                        <button
                          type="button"
                          className="gallery-report-confirm"
                          onClick={() => reportGalleryItem(item.id)}
                        >
                          Report
                        </button>
                        <button
                          type="button"
                          className="gallery-report-cancel"
                          onClick={() => setReportingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="gallery-prompt">"{item.prompt}"</div>
                <div className="gallery-meta">
                  <div className="gallery-artist">
                    <span className="artist-dot" style={{ background: ARTIST_COLORS[i % ARTIST_COLORS.length] }}></span>
                    {item.artist}
                  </div>
                </div>
              </div>
            )) : (
              <div className="gallery-empty">
                <span className="material-symbols-outlined" style={{ fontSize: 40, opacity: 0.3 }}>palette</span>
                <p>No drawings yet — play a game to fill the gallery!</p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* ═══ CENTER: Home Card ═══ */}
        <div className="center-column">
          <div className="home-card">
            {mode === null ? (
              <>
                {/* Green wordmark bar — Darumadrop One title */}
                <div className="home-hero-bar">
                  <span className="home-hero-wordmark">PICTAPHONE</span>
                </div>

                <div className="home-hero-body">
                  {/* Tin-can telephone hero graphic */}
                  <img
                    className="home-hero-art"
                    src={tincanTelephone}
                    alt="Two tin cans joined by a string"
                  />

                  {/* Headline */}
                  <h1 className="home-title">Pass the doodle around!</h1>

                  {/* Tagline */}
                  <p className="home-tagline">
                    The online telephone game where creativity meets chaos.
                    No app needed—just share a code and play.
                  </p>

                  {/* Create / Join */}
                  <div className="home-actions">
                    <button
                      className="home-button primary"
                      onClick={() => setMode('create')}
                    >
                      <span className="btn-label">
                        <span className="btn-icon"><span className="material-symbols-outlined">add</span></span>
                        Create Room
                      </span>
                    </button>
                    <button
                      className="home-button secondary"
                      onClick={() => setMode('join')}
                    >
                      <span className="btn-label">
                        <span className="btn-icon"><span className="material-symbols-outlined">arrow_forward</span></span>
                        Join Room
                      </span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              /* Step 2: Enter name (+ room code if joining) */
              <form className="home-form" onSubmit={handleSubmit}>
                {inviteRoomCode && mode === 'join' && (
                  <div className="invite-banner">
                    <span className="invite-banner-icon material-symbols-outlined" style={{fontSize: '20px'}}>celebration</span>
                    <span>You've been invited to a game!</span>
                  </div>
                )}
                {error && (
                  <div className="join-error" role="alert">
                    <span className="join-error-icon material-symbols-outlined">error</span>
                    <span>{error}</span>
                  </div>
                )}
                {mode === 'join' && (
                  <div className="input-group">
                    <label className="input-label">Room Code</label>
                    <input
                      type="text"
                      className={`home-input room-code-input${error ? ' home-input--error' : ''}`}
                      placeholder="ABCD"
                      value={roomCode}
                      onChange={(e) => {
                        if (error && clearError) clearError();
                        setRoomCode(e.target.value.toUpperCase());
                      }}
                      maxLength={4}
                      autoComplete="one-time-code"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      data-bwignore
                      readOnly={!!inviteRoomCode}
                      autoFocus={!inviteRoomCode}
                    />
                  </div>
                )}
                <div className="input-group">
                  <label className="input-label">Your Name</label>
                  <input
                    type="text"
                    className="home-input"
                    placeholder="What should we call you?"
                    value={playerName}
                    onChange={(e) => {
                      if (error && clearError) clearError();
                      setPlayerName(e.target.value);
                    }}
                    maxLength={20}
                    autoComplete="new-name"
                    data-1p-ignore
                    data-lpignore="true"
                    data-form-type="other"
                    data-bwignore
                    autoFocus={mode === 'create' || !!inviteRoomCode}
                  />
                </div>
                <button
                  type="submit"
                  className="home-submit-btn home-submit-btn--create"
                  disabled={
                    !playerName.trim() || (mode === 'join' && !roomCode.trim())
                  }
                >
                  {mode === 'create' ? 'Create Room →' : 'Join Room →'}
                </button>
                <button
                  type="button"
                  className="home-back-btn"
                  onClick={handleBack}
                >
                  ← Back
                </button>
                <p className="home-legal-consent">
                  By {mode === 'create' ? 'creating' : 'joining'} a room you agree to our{' '}
                  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Use</a> and{' '}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
              </form>
            )}

          </div>
        </div>

        {/* ═══ RIGHT PANEL: Stats + Activity ═══ */}
        {mode === null && (
        <div className="side-panel side-panel-right stats-panel">
          <div className="panel-header">
            <div className="panel-header-icon stats-header-icon">
              <span className="material-symbols-outlined">bar_chart</span>
            </div>
            <h2>Live Stats</h2>
          </div>

          {/* Stat cards */}
          <div className="stat-cards">
            <div className="stat-card">
              <div className="stat-icon stat-icon-blue">
                <span className="material-symbols-outlined">stadia_controller</span>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.games_completed.toLocaleString()}</div>
                <div className="stat-label">Games completed</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-coral">
                <span className="material-symbols-outlined">brush</span>
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.drawings_created.toLocaleString()}</div>
                <div className="stat-label">Drawings created</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-green">
                <span className="material-symbols-outlined">group</span>
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  {stats.players_online}
                  <span className="stat-online-dot"></span>
                </div>
                <div className="stat-label">Players online now</div>
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div className="activity-section">
            <div className="activity-header">
              <span className="material-symbols-outlined">bolt</span>
              <h3>Live Activity</h3>
            </div>

            <div className="activity-feed">
              {stats.activity.length > 0 ? stats.activity.map((item, i) => (
                <div className="feed-item" key={i}>
                  <div
                    className="feed-avatar"
                    style={{ background: `linear-gradient(135deg, ${item.avatar_color}88, ${item.avatar_color})` }}
                  >
                    {item.initial}
                  </div>
                  <div className="feed-body">
                    <div className="feed-text">
                      {item.bold ? <strong>{item.bold}</strong> : null}
                      {item.bold ? ' ' : ''}{item.text}
                    </div>
                    <div className="feed-time">{timeAgo(item.timestamp)}</div>
                  </div>
                </div>
              )) : (
                <div className="feed-empty">No activity yet — start a game!</div>
              )}
            </div>
          </div>
        </div>
        )}

      </div>

      <footer className="home-footer">
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Use</a>
        <span className="home-footer-sep">·</span>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </footer>
    </div>
  );
}

export default Home;
